import React, { useState } from "react";
import { format } from "date-fns";
import { MessageSquare } from "lucide-react";
import Chat from "./Chat";

const ApplicationStatus = ({ application }) => {
  const [activeTab, setActiveTab] = useState("application");
  const [staffUserId, setStaffUserId] = useState(null);

  // Safely extract staffUserId when application data is available
  React.useEffect(() => {
    if (application && application.assignedTo && application.assignedTo._id) {
      setStaffUserId(application.assignedTo._id);
    }
  }, [application]);

  const getFormattedDate = (dateString) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "dd MMM, yyyy");
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  const getFormattedDateTime = (dateString) => {
    if (!dateString) return "";
    try {
      return format(new Date(dateString), "dd MMM, yyyy - h:mm a");
    } catch (error) {
      console.error("Error formatting date time:", error);
      return "";
    }
  };

  // Get dot color based on status
  const getDotColor = (status) => {
    switch (status) {
      case "rejected":
        return "bg-red-500";
      case "approved":
      case "loan-issued":
      case "loan-closed":
        return "bg-green-500";
      case "processing":
      case "document-verification":
      case "credit-review":
      case "banking-partner":
      case "customer-response":
      case "customer-notification":
        return "bg-blue-500";
      case "in-review":
      case "document-review":
        return "bg-yellow-500";
      case "completed":
        return "bg-purple-500";
      default:
        return "bg-[#FFCA20]";
    }
  };

  // Generate comprehensive loan flow timeline
  const generateLoanFlowTimeline = () => {
    if (!application) return [];

    const timelineEntries = [];
    let orderIndex = 0;

    // 1. Loan Applied
    if (application?.createdAt) {
      timelineEntries.push({
        status: "loan-applied",
        description: "Loan Applied",
        details: [
          "Customer submitted loan application",
          getFormattedDateTime(application.createdAt),
        ],
        timestamp: application.createdAt,
        order: orderIndex++,
        icon: "📝",
      });
    }

    // 2. Documents Submitted
    if (application?.documents && application.documents.length > 0) {
      timelineEntries.push({
        status: "documents-submitted",
        description: "All Required Documents Submitted",
        details: [
          `${application.documents.length} documents uploaded`,
          getFormattedDateTime(application.updatedAt || application.createdAt),
        ],
        timestamp: application.updatedAt || application.createdAt,
        order: orderIndex++,
        icon: "📄",
      });
    }

    // 3. Lead Assigned
    if (application?.assignedTo?.name) {
      timelineEntries.push({
        status: "assigned",
        description: `Lead Assigned to ${application.assignedTo.name}`,
        details: [
          `${application.assignedTo.name} assigned this lead`,
          getFormattedDateTime(application.assignedAt || application.createdAt),
        ],
        timestamp: application.assignedAt || application.createdAt,
        order: orderIndex++,
        icon: "👤",
      });
    }

    // 4. Staff Document Review
    if (application?.status === "in-review") {
      timelineEntries.push({
        status: "document-review",
        description: "Staff Reviewed All Documents",
        details: [
          "Staff member reviewed and verified all submitted documents",
          getFormattedDateTime(application.updatedAt || application.createdAt),
        ],
        timestamp: application.updatedAt || application.createdAt,
        order: orderIndex++,
        icon: "🔍",
      });
    }

    // 5. Document Verification
    if (
      application?.status === "processing" &&
      application?.processingMethod === "Internal"
    ) {
      timelineEntries.push({
        status: "document-verification",
        description: "Documents Verified",
        details: [
          "All documents verified and validated",
          getFormattedDateTime(application.updatedAt || application.createdAt),
        ],
        timestamp: application.updatedAt || application.createdAt,
        order: orderIndex++,
        icon: "✅",
      });
    }

    // 6. Sent to Credit Manager
    if (application?.status === "approved") {
      timelineEntries.push({
        status: "credit-review",
        description: "Sent to Credit Manager",
        details: [
          application?.approvedBy?.name
            ? `Application forwarded by ${application.approvedBy.name}`
            : "Application forwarded to credit manager",
          getFormattedDateTime(application.approvedAt || application.updatedAt),
        ],
        timestamp:
          application.approvedAt ||
          application.updatedAt ||
          application.createdAt,
        order: orderIndex++,
        icon: "🏦",
      });
    }

    // 7. Sent to Banking Partners
    if (application?.sentToBank) {
      // Check if this is a reprocessing scenario
      const isReprocessing = application?.statusHistory?.some(
        entry => entry.status === "processing" && entry.reprocessedAt
      );
      
      timelineEntries.push({
        status: "banking-partner",
        description: isReprocessing 
          ? "Application Resent for Bank Approval" 
          : "Application Sent for Bank Approval Successfully",
        details: [
          isReprocessing
            ? "Application resubmitted to banking partners after review"
            : "Application forwarded to banking partners for processing",
          getFormattedDateTime(application.loanDistribution?.createdAt || application.createdAt),
        ],
        timestamp: application.loanDistribution?.createdAt || application.createdAt,
        order: orderIndex++,
        icon: "🏛️",
      });
    }

    // 8. Loan Details Sent to Customer
    if (application?.BanksApprovalDetails && application.BanksApprovalDetails.length > 0) {
      // Check if any bank details were sent to customer
      const sentDetails = application.BanksApprovalDetails.filter(detail => 
        detail.bankName && detail.approvedAmount
      );
      
      if (sentDetails.length > 0) {
        const sentTimestamp = sentDetails[0]?.timestamp || application.detailsSentAt || application.updatedAt || application.createdAt;
        timelineEntries.push({
          status: "customer-notification",
          description: "Loan Details Sent to Customer",
          details: [
            `Bank offer details sent to customer (${sentDetails.length} ${sentDetails.length === 1 ? 'offer' : 'offers'})`,
            getFormattedDateTime(sentTimestamp),
          ],
          timestamp: sentTimestamp,
          order: orderIndex++,
          icon: "📧",
        });
      }
    }

    // 9. Customer Response
    if (application?.BanksApprovalDetails && application.BanksApprovalDetails.length > 0) {
      // Check for customer responses in BanksApprovalDetails
      const customerResponses = application.BanksApprovalDetails.filter(detail => 
        detail.customerAccepted !== undefined || detail.customerRejected !== undefined
      );
      
      customerResponses.forEach(response => {
        if (response.customerAccepted) {
          timelineEntries.push({
            status: "approved",
            description: "Customer Approved Loan Offer",
            details: [
              `Customer accepted loan offer from ${response.bankName || 'selected bank'}`,
              getFormattedDateTime(response.customerAcceptedAt || response.timestamp || application.updatedAt),
            ],
            timestamp: response.customerAcceptedAt || response.timestamp || application.updatedAt,
            order: orderIndex++,
            icon: "👍",
          });
        } else if (response.customerRejected) {
          timelineEntries.push({
            status: "rejected",
            description: "Customer Rejected Loan Offer",
            details: [
              `Customer declined loan offer from ${response.bankName || 'selected bank'}`,
              response.customerRejectionReason ? `Reason: ${response.customerRejectionReason}` : null,
              getFormattedDateTime(response.customerRejectedAt || response.timestamp || application.updatedAt),
            ].filter(Boolean),
            timestamp: response.customerRejectedAt || response.timestamp || application.updatedAt,
            order: orderIndex++,
            icon: "👎",
          });
        }
      });
    }

    // 10. Loan Issued
    if (application?.status === "loan-issued") {
      timelineEntries.push({
        status: "loan-issued",
        description: "Loan Issued",
        details: [
          "Credit manager has issued the loan",
          application?.loanAmount ? `Amount: ₹${application.loanAmount}` : null,
          getFormattedDateTime(
            application.loanIssuedAt || application.updatedAt
          ),
        ].filter(Boolean),
        timestamp: application.loanIssuedAt || application.updatedAt,
        order: orderIndex++,
        icon: "💰",
      });
    }

    // 11. Final Status - Approved/Rejected
    if (application?.status === "approved" && !application?.loanIssuedAt) {
      timelineEntries.push({
        status: "approved",
        description: "Application Approved",
        details: [
          "Application has been approved for processing",
          getFormattedDateTime(application.approvedAt || application.updatedAt),
        ],
        timestamp: application.approvedAt || application.updatedAt,
        order: orderIndex++,
        icon: "✅",
      });
    }

    if (application?.status === "rejected") {
      timelineEntries.push({
        status: "rejected",
        description: "Application Rejected",
        details: [
          application?.rejectedBy?.name
            ? `Rejected by ${application.rejectedBy.name}`
            : "Application has been rejected",
          application?.rejectionReason
            ? `Reason: ${application.rejectionReason}`
            : null,
          getFormattedDateTime(application.rejectedAt || application.updatedAt),
        ].filter(Boolean),
        timestamp: application.rejectedAt || application.updatedAt,
        order: orderIndex++,
        icon: "❌",
      });
    }

    // 12. Loan Closed
    if (
      application?.status === "closed" ||
      application?.status === "completed"
    ) {
      timelineEntries.push({
        status: "loan-closed",
        description: "Loan Closed",
        details: [
          "Loan process has been completed and closed",
          getFormattedDateTime(application.closedAt || application.updatedAt),
        ],
        timestamp: application.closedAt || application.updatedAt,
        order: orderIndex++,
        icon: "🔒",
      });
    }

    // Sort by timestamp (most recent first), then by order for same timestamps
    return timelineEntries.sort((a, b) => {
      const timeDiff = new Date(b.timestamp) - new Date(a.timestamp);
      if (timeDiff === 0) {
        return b.order - a.order; // Maintain predefined order for same timestamps
      }
      return timeDiff;
    });
  };

  const timelineEntries = generateLoanFlowTimeline();

  // Don't proceed with timeline entries if application is not available
  if (!application) {
    return (
      <div className="bg-[#131313] rounded-lg p-4 mb-4">
        <p className="text-gray-400 text-center">
          Loading application status...
        </p>
      </div>
    );
  }

  return (
    <div className="bg-[#131313] rounded-lg p-4 mb-4">
      {/* Tabs */}
      <div className="flex flex-wrap justify-between mb-4 gap-2">
        <button
          onClick={() => setActiveTab("application")}
          className={`px-4 py-2 rounded-full flex items-center ${
            activeTab === "application"
              ? "bg-[#FFCA20] text-black"
              : "text-[#ffca20] border-2 border-[#ffca20]"
          }`}
        >
          <MessageSquare className="w-4 h-4 mr-1" />
          Application Flow
        </button>

        <button
          onClick={() => setActiveTab("chat")}
          className={`px-4 py-2 rounded-full flex items-center ${
            activeTab === "chat"
              ? "bg-[#FFCA20] text-black"
              : "text-[#ffca20] border-2 border-[#ffca20]"
          }`}
        >
          <MessageSquare className="w-4 h-4 mr-1" />
          Chat
        </button>
      </div>

      {/* Timeline Section */}
      {activeTab === "application" ? (
        <div className="pl-6 relative">
          {/* Timeline vertical line */}
          <div className="absolute left-2 top-2 bottom-0 w-0.5 bg-gray-700"></div>

          {timelineEntries.length > 0 ? (
            timelineEntries.map((entry, index) => (
              <div key={index} className="mb-6 relative">
                {/* Colored Dot */}
                <div
                  className={`absolute -left-6 mt-1 w-4 h-4 rounded-full ${getDotColor(
                    entry.status
                  )}`}
                ></div>

                {/* Icon and Description */}
                <div className="flex items-center mb-1">
                  <span className="text-lg mr-2">{entry.icon}</span>
                  <p className="text-white text-md font-medium">
                    {entry.description}
                  </p>
                </div>

                {/* Details */}
                {entry.details.map((detail, i) => (
                  <p key={i} className="text-gray-400 text-sm ml-7">
                    {detail}
                  </p>
                ))}
              </div>
            ))
          ) : (
            <p className="text-gray-400 text-sm">
              No timeline entries available.
            </p>
          )}
        </div>
      ) : (
        <Chat
          staffUserId={staffUserId}
          customerId={application?.user?._id}
          senderType="Admin"
          receiverType="Customer"
        />
      )}
    </div>
  );
};

export default ApplicationStatus;
